import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import {
  api,
  type Estimation,
  type Expediteur,
  type ApercuModele,
  type ListeDiffusion,
  type Modele,
  type Lot,
  type ResultatLot,
} from '../../api/client';
import {
  ApercuMobile,
  Bandeau,
  Carte,
  EtatVide,
  FilEtapes,
  MessageErreur,
  MessageSucces,
  PuceLibre,
} from '../../components/Ui';
import { formaterDate, formaterFcfa, formaterNombre, libelleEncodage } from '../../lib/format';
import { useEstProduction } from '../../lib/plateforme';

/** Sous ce nombre d'unités restantes, le compteur passe en orange. */
const SEUIL_ALERTE_SEGMENT = 15;

type Etape = 'composer' | 'verifier' | 'termine';

/**
 * Campagnes — composer, vérifier, envoyer.
 *
 * L'étape de vérification n'est pas une politesse : un envoi en masse est en
 * tout ou rien sur le solde, et le coût total doit être vu avant d'être engagé.
 */
export function Campagnes() {
  const estProduction = useEstProduction();
  const [etape, setEtape] = useState<Etape>('composer');
  const [expediteurs, setExpediteurs] = useState<Expediteur[]>([]);
  const [expediteur, setExpediteur] = useState('');
  const [destinataires, setDestinataires] = useState('');
  const [listes, setListes] = useState<ListeDiffusion[]>([]);
  const [listeId, setListeId] = useState('');
  const [modeles, setModeles] = useState<Modele[]>([]);
  const [modeleId, setModeleId] = useState('');
  const [apercu, setApercu] = useState<ApercuModele | null>(null);
  const [corps, setCorps] = useState('');
  const [categorie, setCategorie] = useState<'marketing' | 'transactional'>('marketing');

  const [estimation, setEstimation] = useState<Estimation | null>(null);
  const [lots, setLots] = useState<Lot[]>([]);
  const [resultat, setResultat] = useState<ResultatLot | null>(null);
  const [erreur, setErreur] = useState<unknown>(null);
  const [enCours, setEnCours] = useState(false);

  const numeros = destinataires
    .split(/[\s,;]+/)
    .map((n) => n.trim())
    .filter(Boolean);

  const listeChoisie = listes.find((l) => l.id === listeId) ?? null;
  const modeleChoisi = modeles.find((m) => m.id === modeleId) ?? null;
  const corpsEffectif = modeleChoisi ? modeleChoisi.body : corps;

  /**
   * Ce qui partira réellement. Pour une liste, on affiche les contacts
   * joignables — total moins retraits STOP — parce que c'est le chiffre qui
   * détermine le coût, et qu'annoncer le total serait mentir sur la facture.
   */
  const nombreDestinataires = listeChoisie ? listeChoisie.contacts_reachable : numeros.length;

  /**
   * Ce qui partira vraiment. Avec un modèle, les contacts dont une variable
   * manque sont écartés : les compter serait annoncer un envoi qui n'aura pas
   * lieu, et un coût qui ne sera pas facturé.
   */
  const nombreEnvoyes = apercu ? apercu.complete_recipients : nombreDestinataires;

  // Une liste sans base légale prouvable ne peut pas porter une campagne
  // publicitaire : la loi 025/2023 l'interdit, et le serveur la refusera.
  const listeInterdite =
    listeChoisie !== null && categorie === 'marketing' && !listeChoisie.allows_marketing;

  const chargerLots = () =>
    api
      .lots()
      .then(({ data }) => setLots(data))
      .catch(() => setLots([]));

  useEffect(() => {
    api
      .expediteurs()
      .then(({ data }) => {
        setExpediteurs(data);
        const approuve = data.find((e) => e.status === 'approved');
        if (approuve) setExpediteur((actuel) => actuel || approuve.value);
      })
      .catch(setErreur);
  }, []);

  useEffect(() => {
    void chargerLots();
    api
      .listes()
      .then(({ data }) => setListes(data))
      .catch(() => setListes([]));
    // Seuls les modèles approuvés peuvent porter une campagne : inutile de
    // proposer les autres, le serveur les refuserait.
    api
      .modeles()
      .then(({ data }) => setModeles(data.filter((m) => m.usable)))
      .catch(() => setModeles([]));
  }, []);

  // Aperçu du rendu : c'est la seule façon de connaître le coût réel d'un
  // modèle à variables, puisque la segmentation dépend de chaque destinataire.
  useEffect(() => {
    if (!modeleId || !listeId) {
      setApercu(null);
      return;
    }
    api
      .apercuModele(modeleId, listeId)
      .then(setApercu)
      .catch(() => setApercu(null));
  }, [modeleId, listeId]);

  // Estimation temporisée : un appel par frappe serait du gaspillage, mais le
  // compteur doit rester perçu comme instantané.
  useEffect(() => {
    const minuterie = setTimeout(() => {
      api
        .estimer(corpsEffectif, Math.max(nombreDestinataires, 1))
        .then(setEstimation)
        .catch(() => setEstimation(null));
    }, 220);
    return () => clearTimeout(minuterie);
  }, [corpsEffectif, nombreDestinataires]);

  async function envoyer(evenement: FormEvent) {
    evenement.preventDefault();
    setErreur(null);
    setEnCours(true);
    try {
      const reponse = await api.envoyerLot(
        listeId
          ? {
              list_id: listeId,
              ...(modeleId ? { template_id: modeleId } : { body: corps }),
              from: expediteur,
              category: categorie,
            }
          : { to: numeros, from: expediteur, body: corps, category: categorie },
      );
      setResultat(reponse);
      setEtape('termine');
      await chargerLots();
    } catch (e) {
      setErreur(e);
      setEtape('composer');
    } finally {
      setEnCours(false);
    }
  }

  const procheDeLaBascule =
    estimation !== null && estimation.remaining_in_segment <= SEUIL_ALERTE_SEGMENT;

  const ETAPES: { cle: Etape; libelle: string }[] = [
    { cle: 'composer', libelle: 'Composer' },
    { cle: 'verifier', libelle: 'Vérifier' },
    { cle: 'termine', libelle: 'Envoyer' },
  ];
  const indexEtape = ETAPES.findIndex((e) => e.cle === etape);

  return (
    <>
      <FilEtapes etapes={ETAPES.map((e) => e.libelle)} index={indexEtape} />

      <MessageErreur erreur={erreur} />

      {etape === 'termine' && resultat && (
        <MessageSucces>
          <div>
            Campagne acceptée — <strong>{formaterNombre(resultat.accepted)}</strong> message(s) en
            file, coût total {formaterFcfa(resultat.total_cost_xaf)}.
            {resultat.opted_out > 0 &&
              ` ${resultat.opted_out} destinataire(s) filtré(s) par la liste STOP, non facturé(s).`}
            {resultat.rejected.length > 0 &&
              ` ${resultat.rejected.length} numéro(s) invalide(s) écarté(s).`}
            {resultat.incomplete.length > 0 &&
              ` ${resultat.incomplete.length} contact(s) écarté(s) faute de données pour une variable.`}
            {resultat.variable_segmentation &&
              ` Segmentation variable : ${resultat.total_segments} segments facturés au total.`}
          </div>
          <button
            className="lien"
            style={{ marginTop: 8 }}
            onClick={() => {
              setResultat(null);
              setEtape('composer');
              setDestinataires('');
              setCorps('');
            }}
          >
            Composer une nouvelle campagne
          </button>
        </MessageSucces>
      )}

      {etape !== 'termine' && (
        <form onSubmit={envoyer}>
          <div className="rangee">
            <div className="large">
              <Carte titre="Composer la campagne">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginBottom: 14 }}>
                  <label className="champ" style={{ flex: '1 1 200px' }}>
                    <span>Expéditeur affiché</span>
                    <select
                      value={expediteur}
                      onChange={(e) => setExpediteur(e.target.value)}
                      required
                    >
                      <option value="">Choisir…</option>
                      {expediteurs.map((e) => (
                        <option key={e.id} value={e.value}>
                          {e.value}
                          {e.status !== 'approved' ? ' — non validé' : ''}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="champ" style={{ flex: '1 1 200px' }}>
                    <span>Nature de l’envoi</span>
                    <select
                      value={categorie}
                      onChange={(e) =>
                        setCategorie(e.target.value as 'marketing' | 'transactional')
                      }
                    >
                      <option value="marketing">Publicitaire — liste STOP appliquée</option>
                      <option value="transactional">Transactionnel — relation contractuelle</option>
                    </select>
                  </label>
                </div>

                <label className="champ" style={{ marginBottom: 14 }}>
                  <span>Destinataires</span>
                  <select
                    value={listeId}
                    onChange={(e) => setListeId(e.target.value)}
                    style={{ marginBottom: 10 }}
                  >
                    <option value="">Saisir les numéros à la main</option>
                    {listes.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.name} — {formaterNombre(l.contacts_reachable)} joignable(s)
                        {l.allows_marketing ? '' : ' · transactionnel seulement'}
                      </option>
                    ))}
                  </select>
                  {listeChoisie && modeles.length > 0 && (
                    <select
                      value={modeleId}
                      onChange={(e) => setModeleId(e.target.value)}
                      style={{ marginBottom: 10 }}
                    >
                      <option value="">Rédiger le message ici</option>
                      {modeles.map((m) => (
                        <option key={m.id} value={m.id}>
                          Modèle approuvé — {m.name}
                          {m.variables.length > 0 ? ` (${m.variables.join(', ')})` : ''}
                        </option>
                      ))}
                    </select>
                  )}
                  {listeChoisie ? (
                    <div className="aide" style={{ marginTop: 0 }}>
                      <strong>{formaterNombre(listeChoisie.contacts_total)}</strong> contact(s) dans
                      la liste, dont{' '}
                      <strong>{formaterNombre(listeChoisie.contacts_opted_out)}</strong> inscrit(s)
                      sur la liste STOP.{' '}
                      <strong>{formaterNombre(listeChoisie.contacts_reachable)}</strong> partiront
                      et seront facturés. Base légale :{' '}
                      <strong>{listeChoisie.legal_basis_label}</strong>.
                    </div>
                  ) : (
                    <>
                      <textarea
                        rows={4}
                        value={destinataires}
                        onChange={(e) => setDestinataires(e.target.value)}
                        placeholder="+241 66 00 12 42, +241 77 00 12 43…"
                        required
                      />
                      <div className="aide">
                        Un numéro par ligne, ou séparés par des virgules. Les doublons sont
                        dédupliqués et facturés une seule fois ; les numéros invalides sont écartés
                        sans bloquer le reste du lot. <strong>{numeros.length}</strong> saisi(s).
                      </div>
                    </>
                  )}
                </label>

                {listeInterdite && (
                  <div className="bandeau rouge" style={{ marginBottom: 14 }}>
                    <b>Cette liste ne peut pas porter une campagne publicitaire.</b>
                    Sa base légale est « {listeChoisie?.legal_basis_label} ». La loi 025/2023 exige
                    un consentement prouvable — opt-in horodaté ou formulaire papier, avec la
                    référence de la preuve. Vous pouvez en revanche lui adresser un message
                    transactionnel.
                  </div>
                )}

                <label className="champ">
                  <span>
                    {modeleChoisi ? `Message — modèle « ${modeleChoisi.name} »` : 'Message'}
                  </span>
                  <textarea
                    rows={5}
                    value={corpsEffectif}
                    onChange={(e) => setCorps(e.target.value)}
                    maxLength={1600}
                    required={!modeleChoisi}
                    readOnly={Boolean(modeleChoisi)}
                    style={modeleChoisi ? { background: 'var(--fond)' } : undefined}
                  />
                  {modeleChoisi && (
                    <div className="aide">
                      Texte approuvé, non modifiable ici. Le changer passerait par une nouvelle
                      relecture — c'est tout l'objet de l'approbation.
                    </div>
                  )}
                  <div className={`compteur ${procheDeLaBascule ? 'proche-bascule' : ''}`}>
                    <span>
                      <strong>{estimation?.characters ?? 0}</strong> caractères
                    </span>
                    <span>
                      <strong>{estimation?.segments ?? 1}</strong> segment(s) facturé(s)
                    </span>
                    <span>
                      Alphabet {estimation ? libelleEncodage(estimation.encoding) : 'GSM-7'}
                    </span>
                    {estimation && (
                      <span>{estimation.remaining_in_segment} restants dans ce segment</span>
                    )}
                  </div>
                  {estimation && estimation.non_gsm7_characters.length > 0 && (
                    <div className="aide" style={{ color: 'var(--orange-fonce)' }}>
                      Caractère non-GSM détecté (
                      <strong>{estimation.non_gsm7_characters.join(' ')}</strong>) : la limite passe
                      à 70 caractères par segment au lieu de 160, et le coût augmente en
                      conséquence.
                    </div>
                  )}
                  {categorie === 'marketing' && (
                    <div className="aide">
                      Envoi publicitaire : la mention de retrait « Répondez STOP » est une
                      obligation de la loi 025/2023.
                    </div>
                  )}
                </label>

                {/*
                  Les deux boutons portent une `key` distincte, et ce n'est pas
                  cosmétique. Sans elle, React reconnaît le même élément à la
                  même position et réutilise le nœud DOM en se contentant d'y
                  changer `type="button"` en `type="submit"`. Le changement est
                  appliqué avant que le navigateur n'évalue l'action par défaut
                  du clic : un seul clic sur « Vérifier » envoyait la campagne,
                  et l'étape de vérification ne protégeait plus rien.
                */}
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 18 }}>
                  {etape === 'composer' ? (
                    <button
                      key="verifier"
                      type="button"
                      className="accent"
                      disabled={
                        // `corpsEffectif`, pas `corps` : avec un modèle, le
                        // texte vient du modèle et le champ de saisie reste vide.
                        !corpsEffectif || nombreDestinataires === 0 || !expediteur || listeInterdite
                      }
                      onClick={() => setEtape('verifier')}
                    >
                      Vérifier avant envoi
                    </button>
                  ) : (
                    <>
                      <button key="envoyer" type="submit" className="accent" disabled={enCours}>
                        {enCours
                          ? 'Envoi…'
                          : estProduction
                            ? `Envoyer ${formaterNombre(nombreEnvoyes)} message(s)`
                            : 'Envoyer sur la plateforme de test'}
                      </button>
                      <button
                        key="retour"
                        type="button"
                        className="secondaire"
                        onClick={() => setEtape('composer')}
                      >
                        Revenir à la composition
                      </button>
                    </>
                  )}
                </div>
              </Carte>
            </div>

            <div className="etroit" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/*
                Avec un modèle à variables, le coût ne se déduit pas d'un
                compteur de caractères : il faut rendre le message pour chaque
                contact. On montre le pire cas, pas une moyenne — c'est lui qui
                fait déborder un segment, et donc qui surprend sur la facture.
              */}
              {apercu && (
                <Carte titre="Aperçu du rendu">
                  {apercu.variable_segmentation && (
                    <div className="bandeau orange" style={{ marginBottom: 12, fontSize: 13 }}>
                      <b>Le nombre de segments varie d’un destinataire à l’autre.</b>
                      Les variables allongent le message inégalement : le total facturé est la somme
                      des segments réels, pas un prix unitaire multiplié.
                    </div>
                  )}

                  <dl className="definitions">
                    <dt>Destinataires complets</dt>
                    <dd>{formaterNombre(apercu.complete_recipients)}</dd>
                    {apercu.incomplete_count > 0 && (
                      <>
                        <dt>Écartés — variable absente</dt>
                        <dd style={{ color: 'var(--rouge)' }}>
                          {formaterNombre(apercu.incomplete_count)}
                        </dd>
                      </>
                    )}
                    {apercu.best_case && (
                      <>
                        <dt>Plus court rendu</dt>
                        <dd>{apercu.best_case.segments} segment(s)</dd>
                      </>
                    )}
                    {apercu.worst_case && (
                      <>
                        <dt>Plus long rendu</dt>
                        <dd style={{ fontWeight: 500 }}>{apercu.worst_case.segments} segment(s)</dd>
                      </>
                    )}
                    <dt>Segments facturés</dt>
                    <dd>
                      <strong>{formaterNombre(apercu.total_segments)}</strong>
                    </dd>
                    <dt>Total hors taxes</dt>
                    <dd style={{ color: 'var(--rouge)', fontWeight: 500 }}>
                      {formaterFcfa(apercu.total_cost_xaf)}
                    </dd>
                  </dl>

                  {apercu.samples.length > 0 && (
                    <div style={{ marginTop: 14 }}>
                      <div className="libelle-champ">Exemples de rendu</div>
                      {apercu.samples.map((exemple) => (
                        <div
                          key={exemple.msisdn}
                          style={{
                            background: 'var(--fond)',
                            border: '1px solid var(--bordure)',
                            borderRadius: 8,
                            padding: '9px 11px',
                            fontSize: 12.5,
                            marginBottom: 6,
                            textWrap: 'pretty',
                          }}
                        >
                          {exemple.corps}
                          <div style={{ color: 'var(--muet)', fontSize: 11.5, marginTop: 4 }}>
                            {exemple.segments} segment(s)
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {apercu.incomplete.length > 0 && (
                    <div className="aide">
                      Contacts écartés :{' '}
                      {apercu.incomplete
                        .slice(0, 5)
                        .map((i) => i.to)
                        .join(', ')}
                      {apercu.incomplete.length > 5 ? '…' : ''} — variable(s) manquante(s) :{' '}
                      {[...new Set(apercu.incomplete.flatMap((i) => i.missing))].join(', ')}.
                      Complétez ces contacts dans la liste, ou ils ne recevront rien.
                    </div>
                  )}
                </Carte>
              )}

              {!apercu && (
                <Carte titre="Coût estimé">
                  <div
                    style={{
                      background: 'var(--fond)',
                      border: '1px solid var(--bordure)',
                      borderRadius: 8,
                      padding: '14px 16px',
                    }}
                  >
                    {[
                      [
                        listeChoisie ? 'Destinataires joignables' : 'Destinataires saisis',
                        formaterNombre(nombreDestinataires),
                      ],
                      ['Segments par message', String(estimation?.segments ?? 1)],
                      [
                        'Segments facturés',
                        formaterNombre((estimation?.segments ?? 1) * nombreDestinataires),
                      ],
                      ['Prix unitaire', formaterFcfa(estimation?.price_per_segment_xaf ?? 0)],
                    ].map(([libelle, valeur]) => (
                      <div
                        key={libelle}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: 12,
                          padding: '5px 0',
                          fontSize: 13,
                        }}
                      >
                        <span>{libelle}</span>
                        <span className="mono">{valeur}</span>
                      </div>
                    ))}
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 12,
                        padding: '10px 0 0',
                        marginTop: 7,
                        borderTop: '1px solid var(--bordure)',
                        fontWeight: 500,
                      }}
                    >
                      <span>Total hors taxes</span>
                      <span className="mono" style={{ color: 'var(--rouge)' }}>
                        {formaterFcfa(estimation?.total_cost_xaf ?? 0)}
                      </span>
                    </div>
                  </div>
                  <div className="aide">
                    Débité du solde à l’acceptation par l’opérateur, pas à l’acceptation de la
                    requête. Un message non remis est recrédité automatiquement sous 24 heures.
                    {!estProduction &&
                      ' Sur la plateforme de test, le coût affiché est nul : aucun crédit n’est consommé.'}
                  </div>
                </Carte>
              )}

              <Carte titre="Aperçu sur mobile">
                <ApercuMobile expediteur={expediteur} corps={corps} />
              </Carte>
            </div>
          </div>
        </form>
      )}

      <div style={{ marginTop: 16 }}>
        <Carte titre="Campagnes récentes" sansCorps>
          {lots.length === 0 ? (
            <EtatVide titre="Aucune campagne envoyée.">
              <div className="aide" style={{ marginTop: 0 }}>
                Les campagnes sont dérivées des lots réellement émis — il n’y a pas de table séparée
                à maintenir cohérente avec les messages.
              </div>
            </EtatVide>
          ) : (
            <div className="tableau-conteneur">
              <table style={{ minWidth: 700 }}>
                <thead>
                  <tr>
                    <th>Message</th>
                    <th>Expéditeur</th>
                    <th>Émission</th>
                    <th className="droite">Volume</th>
                    <th className="droite">Remise</th>
                    <th className="droite">Coût</th>
                    <th>État</th>
                  </tr>
                </thead>
                <tbody>
                  {lots.map((lot) => {
                    const acheves = lot.delivered + lot.failed;
                    const taux =
                      acheves > 0 ? `${Math.round((lot.delivered / acheves) * 1000) / 10} %` : '—';
                    return (
                      <tr key={lot.batch_id}>
                        <td style={{ maxWidth: 260 }}>
                          <div
                            style={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                            title={lot.body}
                          >
                            {lot.body}
                          </div>
                        </td>
                        <td>
                          <span className="etiquette-sid">{lot.from}</span>
                        </td>
                        <td className="mono" style={{ color: 'var(--muet)', fontSize: 12.5 }}>
                          {formaterDate(lot.created_at)}
                        </td>
                        <td className="nombre">{formaterNombre(lot.total)}</td>
                        <td className="nombre">{taux}</td>
                        <td className="nombre">{formaterFcfa(lot.cost_xaf)}</td>
                        <td>
                          {lot.pending > 0 ? (
                            <PuceLibre ton="attente">
                              En cours — {formaterNombre(lot.pending)}
                            </PuceLibre>
                          ) : (
                            <PuceLibre ton="ok">Terminée</PuceLibre>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Carte>
      </div>

      {!estProduction && (
        <div style={{ marginTop: 16 }}>
          <Bandeau titre="Plateforme de test — aucun message réel n’est émis.">
            Les envois sont simulés de bout en bout, votre solde n’est pas débité et aucune écriture
            n’est portée au grand livre.
          </Bandeau>
        </div>
      )}
    </>
  );
}
